import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { Button } from '@/components/common/Buttons';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { RichHeading } from '@/components/common/Heading';
import { Heading, HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { Section } from '@/components/layout/Section';
import { getButtonComponentThemeSolid } from '@/sanity/schemas/objects/colours/colourUtils';
import { blockUrlWithAudience } from '@/lib/utils';
import HexagonLogo from '@/public/images/hexagonLogo.svg';
import { subheadingLarge } from '@/styles/typography';
import type {
  BlockWithAudience,
  PageData,
  ProgrammeType,
} from '@/types/shared';

import type { MultipointColumnExplainer as SanityMultipointColumnExplainer } from '@/sanity/sanity.types'
export interface MultipointColumnExplainerProps
  extends Omit<SanityMultipointColumnExplainer, 'ctaUrl'>,
  BlockWithAudience {
  ctaUrl?: PageData | ProgrammeType;
}

/**
The headings of the 'Explainer columns' can be Small, Medium or Large. For the US professional apprenticeship definition use case the headings should be Small.
*/

export const MultipointColumnExplainer = ({
  showHeadingIllustration,
  heading,
  subtitle,
  backgroundColour,
  blockSpacing,
  paddingAdjustment,
  columns,
  ctaLabel,
  ctaUrl,
  ctaAnchor,
  trackingTag,
  pageAudience,
  isAudienceSwitcherEnabled,
}: MultipointColumnExplainerProps) => {
  const backgroundColourToken = backgroundColour?.token;

  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={backgroundColourToken}
      className="multipoint-column-explainer"
    >
      <HeadingLevel>
        <MultipointColumnContainer>
          {!!showHeadingIllustration && (
            <HeadingIllustration>
              <LogoContainer>
                <HexagonLogo />
              </LogoContainer>
              {!!subtitle && <Subtitle>{subtitle}</Subtitle>}
            </HeadingIllustration>
          )}
          <HeadingContainer>
            <RichHeading heading={heading} size="heading-lg" />
          </HeadingContainer>
          <ColumnContainer>
            {columns.map((column) => (
              <Column key={column._key}>
                <HeadingLevel>
                  <StyledCaptionHeadingDescription
                    heading={column.heading}
                    description={column.subheading}
                    defaultHeadingSize="subheading-lg"
                    desktopAlignment="center"
                    mobileAlignment="center"
                  />
                  <Paragraph size="subheading-sm">{column.body}</Paragraph>
                </HeadingLevel>
              </Column>
            ))}
          </ColumnContainer>
          {!!ctaAnchor && !!ctaLabel && (
            <ButtonContainer>
              <Button
                href={`#${ctaAnchor}`}
                variant={getButtonComponentThemeSolid(backgroundColourToken)}
                className={
                  trackingTag?.blockName &&
                  `tracking-${trackingTag.blockName}__${trackingTag.blockAction}`
                }
              >
                {ctaLabel}
              </Button>
            </ButtonContainer>
          )}
          {!!ctaUrl && !ctaAnchor && !!ctaLabel && (
            <ButtonContainer>
              <Button
                variant={getButtonComponentThemeSolid(backgroundColourToken)}
                href={blockUrlWithAudience(
                  ctaUrl.slug.current,
                  ctaUrl.audienceRef,
                  pageAudience,
                  false,
                  isAudienceSwitcherEnabled,
                )}
              >
                {ctaLabel}
              </Button>
            </ButtonContainer>
          )}
        </MultipointColumnContainer>
      </HeadingLevel>
    </Section>
  );
};

const StyledCaptionHeadingDescription = styled(CaptionHeadingDescription)`
  margin-bottom: var(--spacing-200);
  p {
    ${subheadingLarge}
  }
`;
const HeadingIllustration = styled.div`
  margin: auto;
  font-weight: bold;
  padding-bottom: var(--spacing-500);

  ${screen.md} {
    width: 850px;
  }
`;

const LogoContainer = styled.div`
  width: 90px;
  margin: auto;
  margin-bottom: var(--spacing-small);
  margin-top: -80px;
`;

const HeadingContainer = styled.div`
  margin: 0 auto;

  ${screen.md} {
    max-width: 850px;
  }
`;

const Subtitle = styled.div`
  margin: var(--spacing-500) auto 0 auto;
`;

const MultipointColumnContainer = styled.div`
  text-align: center;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;

  margin-top: var(--spacing-xx-large);

  ${screen.sm} {
    flex-direction: row;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;

  ${Heading} {
    margin-bottom: var(--spacing-xx-small);

    ${screen.sm} {
      flex: 0 0 40px;
    }
  }

  ${screen.sm} {
    width: 30%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;

  ${screen.md} {
    margin-top: var(--spacing-800);
    padding-top: var(--spacing-300);
  }
`;
